import type { SelectChangeEvent } from "@mui/material";
import { CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import apiRoutes from "@/constants/api-routes";
import { VARIABLE_DATA_TYPE_OPTIONS, type VariableDataTypeOption } from "@/constants/rule-variables";
import useAuthHeader from "@/hooks/useAuthHeader";

import { canvasSelectSx } from "../forms/styled/CanvasFormTextField.styled";
import {
	mapExpressionDataTypeToOption,
	normalizeDataTypesApiPayload,
} from "../utils/expression-metadata-utils";

interface DataTypeFieldProps {
	value: number | null;
	onChange: (id: number | null, selectedOption?: VariableDataTypeOption | null) => void;
	/** When provided (non-empty), skips the getDataTypes request and uses this list. */
	options?: VariableDataTypeOption[];
}

/** API may return string ids; options use number — Select value matching must still align. */
const idEquals = (a: number | undefined | null, b: number | undefined | null) =>
	a != null && b != null && Number(a) === Number(b);

const orphanMenuLabel = (id: number, optionList: VariableDataTypeOption[]) =>
	optionList.find((opt) => idEquals(opt.id, id))?.label ?? `Data type (${id})`;

export const DataTypeField = ({
	value,
	onChange,
	options: optionsFromProps,
}: DataTypeFieldProps) => {
	const authHeader = useAuthHeader();
	const [fetchedOptions, setFetchedOptions] = useState<VariableDataTypeOption[]>([]);
	const [loading, setLoading] = useState(!optionsFromProps?.length);

	const useStaticOptions = Boolean(optionsFromProps?.length);

	const options = useMemo(() => {
		if (useStaticOptions) return optionsFromProps!;
		return fetchedOptions.length > 0 ? fetchedOptions : VARIABLE_DATA_TYPE_OPTIONS;
	}, [useStaticOptions, optionsFromProps, fetchedOptions]);
	useEffect(() => {
		if (useStaticOptions) {
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);

		const load = async () => {
			try {
				const response = await axios.get(apiRoutes.getDataTypes, { headers: authHeader });
				if (cancelled) return;
				const list = normalizeDataTypesApiPayload(response.data);
				const mapped = list
					.map(mapExpressionDataTypeToOption)
					.filter((o): o is VariableDataTypeOption => o != null);
				setFetchedOptions(mapped.length > 0 ? mapped : VARIABLE_DATA_TYPE_OPTIONS);
			} catch (newError) {
				if (!cancelled) {
					console.error("Failed to load data types", newError);
					setFetchedOptions(VARIABLE_DATA_TYPE_OPTIONS);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		void load();
		return () => {
			cancelled = true;
		};
	}, [authHeader, useStaticOptions]);

	const valueNotInList = value != null && !options.some((opt) => idEquals(opt.id, value));

	const handleChange = (event: SelectChangeEvent<string>) => {
		const id = Number(event.target.value);
		const opt = options.find((o) => idEquals(o.id, id)) ?? null;
		onChange(Number.isFinite(id) ? id : null, opt);
	};

	const selectValue = value == null ? "" : String(value);

	const renderSelectValue = (v: string) => {
		if (v === "") return "";
		const id = Number(v);
		if (!Number.isFinite(id)) return "";
		const opt = options.find((o) => idEquals(o.id, id));
		if (opt) return opt.label;
		return orphanMenuLabel(id, options);
	};

	return (
		<FormControl fullWidth sx={canvasSelectSx}>
			<InputLabel id="data-type-field-label">Data type</InputLabel>
			<Select<string>
				labelId="data-type-field-label"
				label="Data type"
				value={selectValue}
				onChange={handleChange}
				renderValue={renderSelectValue}
				endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 4 }} /> : undefined}
			>
				{valueNotInList && value != null ? (
					<MenuItem value={String(value)} disabled>
						{orphanMenuLabel(Number(value), options)}
					</MenuItem>
				) : null}
				{options.map((option) => (
					<MenuItem key={option.id} value={String(option.id)}>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};
